<script setup lang="ts">
import { useCurrentUser } from "@/api/current-user";
import { useSiteArchives } from "@/api/site-query";
import { ArchiveTask } from "@soft-db/monitoring-api-client";
import { Badge, Button, makeColumnHelper } from "@soft-db/ui";
import { DateTime } from "luxon";
import AppDataTable from "../common/AppDataTable.vue";
import ArchiveTaskCreationDialog from "./ArchiveTaskCreationDialog.vue";
import ArchiveTaskProgressIndicator from "./ArchiveTaskProgressIndicator.vue";

const { t } = useI18n();

const props = defineProps<{
	siteId: string;
}>();

const { archives, isLoading, refetch } = useSiteArchives(() => props.siteId);

function deterministicRefetch() {
	let needRefresh = false;
	for (const archive of archives.value || []) {
		if (archive.progress == null) {
			needRefresh = true;
		}
	}

	if (needRefresh) {
		refetch();
	}
}

useIntervalFn(() => {
	deterministicRefetch();
}, 1_000);

const { Column } = makeColumnHelper<ArchiveTask>();

const { currentUser } = useCurrentUser();

function formatDateTime(isoTime: string) {
	return DateTime.fromISO(isoTime, { locale: currentUser.value?.preferredLang }).toLocaleString(
		DateTime.DATE_FULL
	);
}

const openCreateDialog = ref(false);
</script>

<template>
	<div>
		<AppDataTable
			layout="simple"
			:data="archives || []"
			:loading="isLoading"
			:sorting="[{ id: 'startTime', desc: false }]"
		>
			<template #action>
				<Button variant="outline" @click="openCreateDialog = true">
					{{ t("archive.createTask") }}
				</Button>
			</template>
			<template #columns>
				<!-- Start Time -->
				<Column
					id="startTime"
					v-slot="{ getValue }"
					enableSorting
					:header="t('dashboard.startTime')"
					:fieldFn="(a) => a.startTime || ''"
				>
					{{ formatDateTime(getValue()) }}
				</Column>

				<!-- End Time -->
				<Column
					id="endTime"
					v-slot="{ getValue }"
					:header="t('dashboard.endTime')"
					:fieldFn="(a) => a.endTime || ''"
				>
					{{ formatDateTime(getValue()) }}
				</Column>

				<!-- Content  -->
				<Column
					id="tasks"
					v-slot="{ getValue }"
					:header="t('archive.content')"
					:fieldFn="(a) => a.progress?.tasks || []"
				>
					<div class="flex gap-2">
						<Badge
							v-if="getValue().includes('PACK_RECORDS')"
							class="truncate"
							size="sm"
							color="secondary"
						>
							{{ t("archive.record") }}
						</Badge>

						<Badge
							v-if="getValue().includes('PACK_EVENTS')"
							class="truncate"
							size="sm"
							color="secondary"
						>
							{{ t("archive.events") }}
						</Badge>
					</div>
				</Column>

				<!-- Progress -->
				<Column id="progress" v-slot="{ row }">
					<ArchiveTaskProgressIndicator :siteId="props.siteId" :task="row.original" />
				</Column>
			</template>
		</AppDataTable>
		<ArchiveTaskCreationDialog v-model:open="openCreateDialog" :siteId="siteId" />
	</div>
</template>
